// Auth context — wraps @clerk/clerk-react and exposes:
//   • user: { user_id, email, name, role, team, supervisor_id, permissions[], regions[], disbursement_limit, picture }
//   • hasPermission(perm) / hasRegion(region) / isAdmin() / hasRole() helpers
//   • login() / logout() / changePassword()
//   • dev-bypass mode for the testing agent (httpOnly sf_bypass_session cookie)
//
// Role/team/permissions live in Clerk public_metadata. The backend /api/me
// endpoint is the authoritative source (it applies role-matrix defaults);
// this context falls back to publicMetadata + the roles catalog while the
// profile loads.
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useAuth as useClerkAuth, useUser as useClerkUser, useClerk } from "@clerk/clerk-react";
import { toast } from "sonner";
import { setTokenGetter } from "@/lib/api";
import apiClient from "@/lib/api";
import { credentialsAvailable } from "@/lib/sessionPolicy";
import { identifyPostHogUser, resetPostHog } from "@/lib/posthog";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// All permission strings — must match backend services/permissions.py
const ALL_PERMISSIONS = [
  "can_manage_users", "can_manage_roles", "can_view_all_cases", "can_view_team_cases",
  "can_view_assigned_cases", "can_assign_anyone", "can_recommend_credit", "can_approve_credit",
  "can_override_risk", "can_disburse", "can_approve_disbursement", "can_view_collections",
  "can_manage_collections_strategy", "can_edit_sales_pipeline",
  "can_view_reports", "can_configure_system", "can_manage_finance", "can_export_data",
  "page_underwriting_hub", "page_applications_inbox", "page_open_banking", "page_sales", "page_legal",
  "page_onboarding_centre", "page_policy_centre", "can_edit_policy", "can_approve_policy", "legal_head",
];

// Regions a user can be granted access to. Must match backend
// services/permissions.py::AVAILABLE_REGIONS. The region switcher in the
// header only surfaces regions in the user's `regions` list.
const ALL_REGIONS = ["UK", "AUS"];

// Roles that always have every region (un-tickable in admin UI). Must
// match backend services/permissions.py::ROLES_WITH_ALL_REGIONS.
const ROLES_WITH_ALL_REGIONS = ["Admin", "Operations Manager"];

// Client-side mirror of backend resolve_effective_regions — used when the
// server profile hasn't loaded yet so the switcher doesn't briefly show
// every region for a UK-only user.
const regionsFromTeam = (team) => {
  if (!team) return [...ALL_REGIONS];
  if (team.includes("[UK]")) return ["UK"];
  if (team.includes("[AU]")) return ["AUS"];
  return [...ALL_REGIONS];
};
const resolveRegions = (role, regionsMd, team) => {
  if (role && ROLES_WITH_ALL_REGIONS.includes(role)) return [...ALL_REGIONS];
  if (Array.isArray(regionsMd) && regionsMd.length) {
    const seen = new Set();
    const out = [];
    for (const r of regionsMd) {
      const up = String(r || "").trim().toUpperCase();
      const norm = up === "AU" ? "AUS" : up;
      if (ALL_REGIONS.includes(norm) && !seen.has(norm)) { out.push(norm); seen.add(norm); }
    }
    if (out.length) return out;
  }
  return regionsFromTeam(team);
};

export function AuthProvider({ children }) {
  const { isLoaded: clerkLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { user: clerkUser } = useClerkUser();
  const clerk = useClerk();

  const [devBypass, setDevBypass] = useState(false);
  const [devUser, setDevUser] = useState(null);
  const [clerkGaveUp, setClerkGaveUp] = useState(false);

  // If clerk-js can't load (e.g. production DNS not live yet), stop blocking
  // the app after a few seconds so the Team-access login stays reachable.
  useEffect(() => {
    if (clerkLoaded) { setClerkGaveUp(false); return undefined; }
    const t = setTimeout(() => setClerkGaveUp(true), 6000);
    return () => clearTimeout(t);
  }, [clerkLoaded]);
  const [rolesCatalog, setRolesCatalog] = useState(null);
  const [serverProfile, setServerProfile] = useState(null);

  // Gate for every authenticated fetch the app makes on its own initiative,
  // including in child providers. It is state rather than a derived value on
  // purpose: it is set *inside* the effect that registers the token getter, so
  // it cannot be true before there is a token to send. Child effects run before
  // parent effects in React, so a child provider reading `isSignedIn` directly
  // would fire its request one commit too early and get a 401 — which is the
  // whole failure this gate exists to prevent.
  const [apiReady, setApiReady] = useState(false);

  // Dev-bypass detection on mount.
  //
  // There is no token to inspect any more — it is in an httpOnly cookie, which
  // is the point. So the marker is the readable CSRF cookie the same login sets:
  // its presence means "a dev-access session cookie exists", and it carries no
  // authority of its own. `sf_user` supplies the display name.
  //
  // This is a *hint*, not a decision. If it is wrong the backend answers 401 and
  // the response interceptor bounces to /login — the session is decided by the
  // cookie the browser sends, never by what this reads.
  useEffect(() => {
    const hasSessionCookie = document.cookie
      .split(";")
      .some((c) => c.trim().startsWith("sf_csrf="));
    const stored = localStorage.getItem("sf_user");
    if (hasSessionCookie && stored) {
      try {
        setDevUser(JSON.parse(stored));
        setDevBypass(true);
      } catch (e) { console.error("Bad dev user:", e); }
    }
  }, []);

  // Wire token getter into our apiClient (axios interceptor)
  useEffect(() => {
    if (devBypass) {
      // No token to hand out: the session is a cookie the browser attaches
      // itself, and the axios interceptor adds no Authorization header when this
      // returns null. Returning the old static `dev-bypass-token` here would put
      // a working Admin credential back into script reach for no benefit.
      //
      // The WebSocket hook consumes this too — see the note on the socket below.
      setTokenGetter(async () => null);
    } else if (isSignedIn) {
      setTokenGetter(async () => {
        try {
          return await getToken();
        } catch (e) {
          console.error("Clerk token fetch failed:", e);
          return null;
        }
      });
    } else {
      setTokenGetter(null);
    }
    // Last in the effect, deliberately — see the note on `apiReady` above.
    setApiReady(credentialsAvailable({ clerkLoaded, isSignedIn, devBypass }));
  }, [clerkLoaded, isSignedIn, devBypass, getToken]);

  // Fetch the role-permission catalog — keeps retrying: a single failed attempt
  // (deploy blip, flaky network) would otherwise leave every Role/Team dropdown
  // empty until a full page reload.
  //
  // Gated on `apiReady`. This route is authenticated like any other staff route,
  // so calling it before a token exists earns a 401 that used to be mistaken for
  // the user's session being rejected.
  useEffect(() => {
    if (!apiReady) return undefined;
    let alive = true;
    let attempt = 0;
    const load = async () => {
      if (!alive) return;
      try {
        const r = await apiClient.get("/admin/users/roles");
        if (alive) setRolesCatalog(r.data);
      } catch (e) {
        attempt += 1;
        console.warn(`Role catalog load failed (attempt ${attempt}, retrying):`, e?.message);
        if (alive) setTimeout(load, Math.min(2000 * attempt, 15000));
      }
    };
    load();
    return () => { alive = false; };
  }, [apiReady]);

  // Fetch server profile when authenticated (authoritative claims)
  useEffect(() => {
    if (devBypass || !isSignedIn) { setServerProfile(null); return; }
    (async () => {
      try {
        const r = await apiClient.get("/me");
        setServerProfile(r.data);
      } catch (e) {
        console.warn("Profile fetch failed:", e);
      }
    })();
  }, [isSignedIn, devBypass]);

  // Keep sf_user in localStorage for the UI's own convenience (display name,
  // and the few components that read it for a default value). It is no longer
  // sent as an identity header — the audit trail reads the verified token — so
  // nothing security-relevant depends on this value any more.
  useEffect(() => {
    if (devBypass || !isSignedIn || !clerkUser) return;
    const email = clerkUser.primaryEmailAddress?.emailAddress || null;
    localStorage.setItem("sf_user", JSON.stringify({
      name: clerkUser.fullName || email,
      email,
    }));
  }, [isSignedIn, clerkUser, devBypass]);

  // Unified user object
  const user = useMemo(() => {
    if (devBypass && devUser) {
      return {
        user_id:    "dev-bypass-user",
        email:      devUser.email || "dev@test.local",
        name:       devUser.name  || "Dev User",
        role:       "Admin",
        roles:      ["Admin"],
        team:       devUser.team || "Finance&Ops",
        supervisor_id: null,
        department: "Admin",
        permissions: ALL_PERMISSIONS,
        regions: [...ALL_REGIONS],
        disbursement_limit: 999999999,
        can_view_payout_balance: true,
        picture: null,
        is_dev_bypass: true,
      };
    }
    if (!clerkUser) return null;

    const md = clerkUser.publicMetadata || {};
    const sp = serverProfile || {};
    const role = sp.role ?? md.role ?? null;

    let permissions = sp.permissions?.length ? sp.permissions : null;
    if (!permissions) {
      const override = Array.isArray(md.permissions_override) && md.permissions_override.length
        ? md.permissions_override : null;
      if (override) permissions = override;
      else if (role === "Admin") permissions = ALL_PERMISSIONS;
      else if (role) permissions = rolesCatalog?.roles?.find(r => r.name === role)?.permissions || [];
      else permissions = [];   // no role assigned → awaiting access
    }

    const email = clerkUser.primaryEmailAddress?.emailAddress || null;
    const team = sp.team || md.team || null;
    const regions = Array.isArray(sp.regions) && sp.regions.length
      ? sp.regions
      : resolveRegions(role, md.regions, team);
    return {
      user_id:            clerkUser.id,
      email,
      name:               clerkUser.fullName || md.name || email,
      picture:            clerkUser.imageUrl,
      email_verified:     true,
      role,
      roles:              role ? [role] : [],
      department:         sp.department || md.department || null,
      team,
      supervisor_id:      sp.supervisor_id || md.supervisor_id || null,
      permissions,
      regions,
      disbursement_limit: sp.disbursement_limit ?? md.disbursement_limit ?? null,
      can_view_payout_balance: Boolean(sp.can_view_payout_balance ?? md.can_view_payout_balance ?? false),
      is_dev_bypass: false,
    };
  }, [clerkUser, devBypass, devUser, serverProfile, rolesCatalog]);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.is_dev_bypass) return true;
    if (user.roles?.includes("Admin")) return true;
    return user.permissions?.includes(perm);
  }, [user]);

  const hasRegion = useCallback((region) => {
    if (!user) return false;
    if (user.is_dev_bypass || user.roles?.includes("Admin")) return true;
    const up = String(region || "").trim().toUpperCase();
    const norm = up === "AU" ? "AUS" : up;
    return (user.regions || []).includes(norm);
  }, [user]);

  // Tie PostHog session replays + events to the logged-in user
  useEffect(() => {
    if (user?.email) identifyPostHogUser(user);
  }, [user?.email, user?.role, user?.team]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdmin = useCallback(() => {
    if (!user) return false;
    return user.is_dev_bypass || user.roles?.includes("Admin") || user.role === "Admin";
  }, [user]);

  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.is_dev_bypass || user.roles?.includes(role) || user.role === role;
  }, [user]);

  const hasAnyRole = useCallback((roles) => {
    if (!user) return false;
    if (user.is_dev_bypass) return true;
    return roles.some(r => user.roles?.includes(r) || user.role === r);
  }, [user]);

  const hasAnyPermission = useCallback((perms) => {
    if (!user) return false;
    if (user.is_dev_bypass) return true;
    if (user.roles?.includes("Admin")) return true;
    return perms.some(p => user.permissions?.includes(p));
  }, [user]);

  const canAssignAnyone = useCallback(() => {
    return hasPermission("can_assign_anyone");
  }, [hasPermission]);

  // Sign-in lives on /login (embedded Clerk <SignIn />)
  const login = useCallback(() => {
    window.location.href = "/login";
  }, []);

  const logout = useCallback(async () => {
    // Revoke first, and await it. Clearing a cookie is a request to the browser;
    // deleting the session row on the server is what makes the token stop
    // working, and anyone who copied the cookie keeps a usable credential until
    // that happens. Navigating away before the call lands would leave the
    // session alive.
    //
    // Best-effort by design: a failed revoke must not trap someone in a session
    // they are trying to leave, so the local sign-out proceeds either way. The
    // server-side session still expires on its own 8-hour TTL.
    try {
      await apiClient.post("/auth/dev-bypass/logout");
    } catch (e) {
      console.warn("Session revoke failed; signing out locally anyway:", e);
    }
    localStorage.removeItem("sf_user");
    resetPostHog();
    setDevBypass(false);
    setDevUser(null);
    if (isSignedIn) {
      signOut({ redirectUrl: "/login" });
    } else {
      window.location.href = "/login";
    }
  }, [isSignedIn, signOut]);

  // Self-service password / account management — opens Clerk's profile modal
  const changePassword = useCallback(() => {
    if (devBypass) {
      toast.info("Dev bypass session — account management isn't available");
      return;
    }
    clerk.openUserProfile();
  }, [clerk, devBypass]);

  const value = {
    user,
    isAuthenticated: devBypass || !!isSignedIn,
    isLoading:       !devBypass && !clerkLoaded && !clerkGaveUp,
    loading:         !devBypass && !clerkLoaded && !clerkGaveUp,  // back-compat alias
    clerkReady:      clerkLoaded,
    // Gate authenticated fetches on this, not on `isAuthenticated` — the latter
    // is true one commit before a token is registered, and a child provider's
    // effect runs in that window.
    apiReady,
    hasPermission,
    hasAnyPermission,
    hasRegion,
    canAssignAnyone,
    isAdmin,
    hasRole,
    hasAnyRole,
    rolesCatalog,           // { roles: [...], teams: [...], permissions: {...} }
    login,
    logout,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
